import { Pressable, ScrollView, Text, View } from "react-native";
import { AddIcon, Icon } from "ui/gluestack/icon";
import { EditableFormFloorDetails } from "../organisms/EditableFormFloorDetails";
import { EditableFormRoomDetails } from "../organisms/EditableFormRoomDetails";
import { EditableFormBedDetails } from "../organisms/EditableFormBedDetails";
import { useFloorManagement } from "../../authentication/hooks/useFloorManagement";
import { useRoomManagement } from "../../authentication/hooks/useRoomManagement";
import { useBedManagement } from "../../authentication/hooks/useBedManagement";
import { useEffect, useState, useRef } from "react";
import { FloorModel } from "../../../services/api/types/locations/models/FloorModel";
import { RoomModel } from "app/services/api/types/locations/models/RoomModel";
import { FloorPlanModalLayout } from "./FloorPlanModalLayout";
import { ResidentBedModel } from "../../../services/api/types/locations/models/ResidentBedModel";
import { Image } from "expo-image";
import clsx from "clsx";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { EditFloorModal, EditFloorModalRef } from "./EditFloorModal";
import { EditRoomModal, EditRoomModalRef } from "./EditRoomModal";
import { EditBedModal, EditBedModalRef } from "./EditBedModal";
import ConfirmationModal, {
  ConfirmationModalRef,
} from "ui/organisms/modals/ConfirmationModal";

export interface Props {
  isVisible: boolean;
  onClose: () => void;
  locationId: number;
  locationName?: string;
}

export function EditFloorPlanModal({
  isVisible,
  locationId,
  locationName,
  onClose,
}: Props) {
  const editFloorModalRef = useRef<EditFloorModalRef>(null);
  const editRoomModalRef = useRef<EditRoomModalRef>(null);
  const editBedModalRef = useRef<EditBedModalRef>(null);
  const confirmationModalRef = useRef<ConfirmationModalRef>(null);

  const [currentFloor, setCurrentFloor] = useState<FloorModel>();
  const [currentRoom, setCurrentRoom] = useState<RoomModel>();

  const { floors, deleteFloor, updateFloor, createFloor } =
    useFloorManagement();

  const { rooms, deleteRoom, updateRoom, createRoom, areRoomsFetching } =
    useRoomManagement({
      floor: currentFloor,
      locationId,
    });

  const { beds, deleteBed, updateBed, createBed, areBedsFetching } =
    useBedManagement({
      room: currentRoom,
      floor: currentFloor,
    });

  const handleFloorSelected = (floor: FloorModel) => {
    setCurrentFloor(floor);
    setCurrentRoom(undefined);
  };

  const handleRoomSelected = (room: RoomModel) => {
    setCurrentRoom(room);
  };

  const handleAddFloor = () => {
    editFloorModalRef.current?.show(undefined);
  };

  const handleAddRoom = () => {
    if (!currentFloor?.id) return;
    editRoomModalRef.current?.show(undefined);
  };

  const handleAddBed = () => {
    if (!currentRoom?.id) return;
    editBedModalRef.current?.show(undefined);
  };

  const handleEditFloor = (floor: FloorModel) => {
    editFloorModalRef.current?.show(floor);
  };

  const handleEditRoom = (room: RoomModel) => {
    editRoomModalRef.current?.show(room);
  };

  const handleEditBed = (bed: ResidentBedModel) => {
    editBedModalRef.current?.show(bed);
  };

  const handleDeleteFloor = async (floorId: number) => {
    const response = await confirmationModalRef.current?.open({
      title: "Delete floor",
      message: "Are you sure you want to delete this floor?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (response) {
      deleteFloor(floorId);
    }
  };

  const handleDeleteRoom = async (roomId: number) => {
    const response = await confirmationModalRef.current?.open({
      title: "Delete room",
      message: "Are you sure you want to delete this room?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (response) {
      deleteRoom(roomId);
    }
  };

  const handleDeleteBed = async (bedId: number) => {
    const response = await confirmationModalRef.current?.open({
      title: "Delete bed",
      message: "Are you sure you want to delete this bed?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (response) {
      deleteBed(bedId);
    }
  };

  const handleBedSave = (
    bedData: {
      bed_number: string;
      description: string;
    },
    bedId?: number,
  ) => {
    if (!currentRoom?.id) return;

    if (bedId) {
      updateBed(bedId, { ...bedData, room_id: currentRoom.id });
    } else {
      createBed({ ...bedData, room_id: currentRoom.id });
    }
    editBedModalRef.current?.hide();
  };

  const handleRoomSave = (
    roomData: {
      name: string;
      room_number: string;
      description: string;
    },
    roomId?: number,
  ) => {
    if (!currentFloor?.id) return;

    if (roomId) {
      updateRoom(roomId, { ...roomData, floor_id: currentFloor.id });
    } else {
      createRoom({ ...roomData, floor_id: currentFloor.id });
    }
    editRoomModalRef.current?.hide();
  };

  const handleFloorSave = (
    floorData: {
      name: string;
      floor_number: string;
      description: string;
    },
    floorId?: number,
  ) => {
    if (floorId) {
      updateFloor(floorId, { ...floorData, location_id: locationId });
    } else {
      createFloor({ ...floorData, location_id: locationId });
    }
    editFloorModalRef.current?.hide();
  };

  return (
    <FloorPlanModalLayout
      isVisible={isVisible}
      onClose={onClose}
      title={locationName}
    >
      <View className="flex-1">
        <View className="flex-row justify-between">
          <View className="flex-1 border-b border-border-200 justify-center">
            <Text className="text-typography-500 px-6 py-4">Floors</Text>
          </View>
          <View className="flex-1 border-l border-r border-b border-border-200 justify-center">
            <Text className="text-typography-500  px-6 py-4">Rooms</Text>
          </View>
          <View className="flex-1 border-b border-border-200 justify-center">
            <Text className="text-typography-500 px-6 py-4">Beds</Text>
          </View>
        </View>

        <View className="flex-row flex-1 justify-between">
          <View className="flex-1">
            <ScrollView>
              {floors?.map((floor: FloorModel, index) => (
                <View key={floor.id} className="mb-2">
                  <EditableFormFloorDetails
                    floor={floor}
                    currentSelectedFloor={currentFloor?.id}
                    onDeleteFloor={() => handleDeleteFloor(floor.id)}
                    onFloorSelected={handleFloorSelected}
                    onEditFloor={handleEditFloor}
                  />
                </View>
              ))}
            </ScrollView>
          </View>
          <View className="flex-1 border-l border-r border-border-200">
            {!currentFloor ? (
              <View className="flex-1 items-center justify-center">
                <Image
                  source={require("app/assets/no_results.png")}
                  className="w-36 h-36"
                />
                <Text className="mt-4 text-center">
                  Select a floor to start adding rooms.
                </Text>
              </View>
            ) : areRoomsFetching ? (
              <View className="flex-1 justify-center items-center bg-white">
                <LoadingSpinner />
              </View>
            ) : currentFloor && rooms?.length === 0 ? (
              <View className="flex-1 items-center justify-center">
                <Image
                  source={require("app/assets/no_results.png")}
                  className="w-36 h-36"
                />
                <Text className="mt-4">No item!</Text>
                <Text className="mt-4 text-center">
                  Click on the `Add Room` <br /> button below to add <br />
                  rooms to this floor.
                </Text>
              </View>
            ) : (
              <ScrollView>
                {rooms?.map((room: RoomModel, index) => (
                  <View key={room.id} className="mb-2">
                    <EditableFormRoomDetails
                      room={room}
                      currentSelectedRoom={currentRoom?.id}
                      onRoomSelected={() => handleRoomSelected(room)}
                      onEditRoom={() => handleEditRoom(room)}
                      onDeleteRoom={() => handleDeleteRoom(room.id)}
                    />
                  </View>
                ))}
              </ScrollView>
            )}
          </View>

          <View className="flex-1">
            {currentFloor && !currentRoom && rooms?.length ? (
              <View className="flex-1 items-center justify-center">
                <Image
                  source={require("app/assets/no_results.png")}
                  className="w-36 h-36"
                />
                <Text className="mt-4 text-center">
                  Select a room to start adding beds.
                </Text>
              </View>
            ) : areBedsFetching ? (
              <View className="flex-1 justify-center items-center bg-white">
                <LoadingSpinner />
              </View>
            ) : currentRoom && beds?.length === 0 ? (
              <View className="flex-1 items-center justify-center">
                <Image
                  source={require("app/assets/no_results.png")}
                  className="w-36 h-36"
                />
                <Text className="mt-4">No item!</Text>
                <Text className="mt-4 text-center">
                  Click on the `Add Bed` <br /> button below to add <br />
                  beds to this room.
                </Text>
              </View>
            ) : (
              <ScrollView>
                {beds?.map((bed: ResidentBedModel, index) => (
                  <View key={bed.id ?? `new-bed-${index}`} className="mb-2">
                    <EditableFormBedDetails
                      bed={bed}
                      roomId={currentRoom?.id}
                      onEditBed={() => handleEditBed(bed)}
                      onDeleteBed={() => handleDeleteBed(bed.id)}
                    />
                  </View>
                ))}
              </ScrollView>
            )}
          </View>
        </View>

        <View className="flex-row mt-auto justify-between">
          <Pressable
            onPress={handleAddFloor}
            className="flex-1 border-t border-border-200 justify-center hover:bg-gray-50"
          >
            <View className="px-6 py-4 flex-row items-center gap-x-2">
              <Text className="text-primary-500 font-semibold">Add Floors</Text>
              <Icon as={AddIcon} className="w-4 h-4 stroke-primary-500"></Icon>
            </View>
          </Pressable>
          <Pressable
            disabled={!currentFloor}
            className="flex-1 border-l border-r border-t border-border-200"
            onPress={handleAddRoom}
          >
            <View className="px-6 py-4 flex-row items-center gap-x-2">
              <Text
                className={clsx(
                  "text-primary-500 font-semibold",
                  !currentFloor ? "text-primary-500/40" : "text-primary-500",
                )}
              >
                Add Room
              </Text>
              <Icon
                as={AddIcon}
                className={clsx(
                  "w-4 h-4 stroke-primary-500 self-center",
                  !currentFloor && !currentRoom
                    ? "stroke-primary-500/40"
                    : "stroke-primary-500",
                )}
              ></Icon>
            </View>
          </Pressable>
          <Pressable
            disabled={!currentRoom?.id}
            className="flex-1 flex-row gap-x-2 border-t border-border-200 hover:bg-gray-50"
            onPress={handleAddBed}
          >
            <View className="px-6 py-4 flex-row items-center gap-x-2">
              <Text
                className={clsx(
                  "text-primary-500 font-semibold",
                  !currentRoom?.id ? "text-primary-500/40" : "text-primary-500",
                )}
              >
                Add Bed
              </Text>
              <Icon
                as={AddIcon}
                className={clsx(
                  "w-4 h-4 stroke-primary-500",
                  !currentRoom?.id
                    ? "stroke-primary-500/40"
                    : "stroke-primary-500",
                )}
              ></Icon>
            </View>
          </Pressable>
        </View>
      </View>

      {/* Floor Modal */}
      <EditFloorModal ref={editFloorModalRef} onSave={handleFloorSave} />

      {/* Room Modal */}
      <EditRoomModal ref={editRoomModalRef} onSave={handleRoomSave} />

      {/* Bed Modal */}
      <EditBedModal ref={editBedModalRef} onSave={handleBedSave} />

      <ConfirmationModal ref={confirmationModalRef} />
    </FloorPlanModalLayout>
  );
}
